import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "es-fullcalendar w-full h-full" }
const _hoisted_2 = {
  key: 0,
  class: "es-spinner marg-s h-full w-full"
}
const _hoisted_3 = { class: "h4" }
const _hoisted_4 = {
  key: 1,
  class: "h-full w-full"
}
const _hoisted_5 = { class: "h4 es-forbidden marg-s h-full w-full" }

import FullCalendar from '@fullcalendar/vue3';
import frLocale from '@fullcalendar/core/locales/fr';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventReceiveArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import { BusinessHoursInput, CalendarOptions, DurationInput, EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core';
import { onMounted, watch, ref, useTemplateRef, computed } from 'vue';
import DateService from '@/services/DateService';
import { useI18n } from 'vue-i18n';
import { CalendarView } from '@/models/calendar.model';
import { PersonApi, PlanningByPeriodResponse } from '@/apiGenerate/medserver';
import { addDays, format, getHours, getMinutes, subMinutes } from 'date-fns';
import { useAppUserStore } from '@/stores/appUser';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { ApiError, DateFormat, ItemTag } from '@/models/share.model';
import { formatTime, getLocaleForJs } from '@/utils/date-utils';
import loadingSpinner from "@/views/loadingSpinner.vue";
import { PlanningRefreshSource } from '@/views/planningMain/enumPlanningRefreshSource';
import SharedService from '@/services/SharedService';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { toastWarn } from '@/utils/toast-utils';
import { useToast } from 'primevue';


export default /*@__PURE__*/_defineComponent({
  __name: 'esContentCalendar',
  props: {
    calendarView: {},
    customRef: {},
    getEvents: { type: Function },
    getBusinessHours: { type: Boolean },
    refresh: { type: Boolean },
    selectedDate: {},
    displayDayHeaders: { type: Boolean },
    doctorId: {},
    isActive: { type: Boolean },
    calcHeight: {},
    displayNowIndicator: { type: Boolean },
    selectedAppointmentType: {},
    startTime: {},
    endTime: {},
    scroll: {},
    isSetting: { type: Boolean },
    changeDate: {},
    changeAgendaType: {},
    changeAppointmentType: {},
    changePatient: {},
    changeDoctor: {},
    changeDoctorList: {},
    eventDurationEditable: { type: Boolean },
    droppable: { type: Boolean },
    displaySettings: { type: Boolean },
    doctorName: {}
  },
  emits: ['refreshed', 'dateClick', 'eventClick', 'exposeApi', 'openSettings', 'updateEvent'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { t } = useI18n();

const personApi = new PersonApi(medServerApiConfig);

const appUserStore = useAppUserStore();
const currentEntryStore = useCurrentEntryStore();
const toast = useToast();

let events: EventInput[] = [];
const planning = ref<PlanningByPeriodResponse[]>([]);
const loading = ref<boolean>(false);
const dateWithSpecialDay = ref<string[]>([]);
const errorMessage = ref<string>(undefined);

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return currentIwr.instituteWithRoleId;
});

const currentAppointmentType = ref<ItemTag>(undefined);

const locale = computed(() => 
    getLocaleForJs(appUserStore?.currentAppUserData?.preferences?.menuLanguageIso)
);

const disabledMoveAppointment = computed(() => {
  return currentEntryStore.moveAppointmentFromPatient || currentEntryStore.moveAppointmentFromCalendar;
});

const calendarOptions = ref<CalendarOptions> ({
        locale: frLocale,
        plugins: [timeGridPlugin, interactionPlugin, dayGridPlugin],
        initialView: props.calendarView,
        expandRows: true,
        height: "100%",
        headerToolbar: {
          left: '',
          center: '',
          right: ''
        },
        events,
        slotEventOverlap: false,
        slotDuration: '01:00:00',
        slotMinTime: '00:00',
        slotMaxTime: '23:59',
        slotLabelFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
          meridiem: 'short'
        },
        dayHeaders: props.displayDayHeaders,
        dayHeaderFormat: {
          weekday: 'long',
          day: 'numeric'
        },
        allDaySlot: false,
        dayHeaderClassNames: 'esCalendarHeader',
        stickyHeaderDates: false,
        eventDurationEditable: props.eventDurationEditable,
        eventResizableFromStart: props.eventDurationEditable,
        editable: props.droppable,
        droppable: props.droppable,
        nowIndicator: props.displayNowIndicator,
        navLinks: props.displaySettings,
        scrollTimeReset: false,
        navLinkDayClick: (date) => {
         openSettingsDay(date);
        },
        dateClick: (info) => {
          emits('dateClick', info);
        },
        dayHeaderContent: (args) => {
            return {html: args.isToday ? formatDayHeaderContent(args.date, true)
                                       : formatDayHeaderContent(args.date, false)
            }
        },
        eventClick: (info: EventClickArg) => {
          if (disabledMoveAppointment.value) {
            toastWarn(toast, {
                summary: t('common.toast.warning'),
                detail: t('error.MOVE_APPOINTMENT_IN_PROGRESS')
            });
          } else if (info.event.display !== 'background') {
            emits('eventClick', info);
          }          
        },
        dayCellClassNames : () => {
          return ['background-white'];
        },
        eventResize: (info: EventResizeDoneArg) => {
          eventResize(info);
        },
        eventDrop: (info: EventDropArg) => {
          eventDrop(info);
        },
        eventReceive: (info: EventReceiveArg) => {
          eventReceive(info);
        }
      });

const emits = __emit;

const calendar = useTemplateRef<InstanceType<typeof FullCalendar>>(props.customRef);

const getIcon = (icon: string, isFillIcon: boolean, subPath: string) => {
  return SharedService.getIcon(icon, isFillIcon, subPath);
};

const openSettingsDay = (settingDate: Date) => {
  emits('openSettings', {date: settingDate, doctorId: props.doctorId});
};
  
const formatDayHeaderContent = (date: Date, isToday: boolean) => {
  let cssClass = "mleft-02";

  if (isToday) {
    cssClass = cssClass + " headerDay";
  }

  return props.displaySettings ? ` <div class="row"> ${DateService.getWeekDayLongFormat(date, locale.value)}
                                            <div class="${cssClass}">${date.getDate()}</div>
                                            <img src="${getIcon('gear', false, 'menuIcons')}" class="${getColorClass(date)}">
                                          </div>`
                                : ` <div class="row"> ${DateService.getWeekDayLongFormat(date, locale.value)}
                                            <div class="${cssClass}">${date.getDate()}</div>
                                          </div>`
}

const getColorClass = (date: Date) => {
  const color: string = dateWithSpecialDay.value.filter((specialDay) => specialDay === format(date, DateFormat.ISO_SMALL)).length > 0 ? 'img-primary' : 'img-grey-dark';

  return 'pleft-02 square-14 ' + color;
}

const toBusinessHour = (planing: PlanningByPeriodResponse): BusinessHoursInput => {
  return {
    daysOfWeek: planing.daysOfWeek,
    startTime: formatTime(planing.startTime),
    endTime: formatTime(planing.endTime)
  }
}

const getPlanning = async () => {
  if (currInstituteUuid.value && props.doctorId) {
    const periodStart = props.calendarView === CalendarView.DAY ? format(props.selectedDate, DateFormat.ISO_SMALL) : format( DateService.getFirstDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);
    const periodEnd = props.calendarView === CalendarView.DAY ? format(addDays(new Date(props.selectedDate), 1), DateFormat.ISO_SMALL) : format( DateService.getLastDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);

    planning.value = await personApi.getPlanningByPerson({
      personId: props.doctorId,
      instituteId: currInstituteUuid.value,
      periodStart: periodStart,
      periodEnd: periodEnd,
      appointmentType: props.selectedAppointmentType?.id ?? undefined
    });

    return planning.value;
  }
}

const getBaseAppointmentDuration = async () => {
  if (currInstituteUuid.value && props.doctorId) {
    const periodStart = props.calendarView === CalendarView.DAY ? 
      format(props.selectedDate, DateFormat.ISO_SMALL) : 
      format(DateService.getFirstDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);

    const periodEnd = props.calendarView === CalendarView.DAY ? 
      format(addDays(new Date(props.selectedDate), 1), DateFormat.ISO_SMALL) :
      format(DateService.getLastDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);

      try {
        const response = await personApi.getPlanningGeneralSettingsByPerson({
          instituteId: currInstituteUuid.value, 
          personId: props.doctorId, 
          periodStart: periodStart, 
          periodEnd: periodEnd
        });

        dateWithSpecialDay.value = response.dateWithSpecialDay;

        if (response.baseAppointmentDurationInMinutes < 10) {
          calendarOptions.value.slotDuration = '00:0' + response.baseAppointmentDurationInMinutes;
        } else {
          calendarOptions.value.slotDuration = '00:' + response.baseAppointmentDurationInMinutes;
        }
      } catch (error) {
        const e = error as ApiError

        if (e.status === 'USER_FORBIDDEN') {
          errorMessage.value = t('calendar.label.forbidden', {'name': props.doctorName});
        } else {
          errorMessage.value = t('error.DEFAULT')
        }
      }
  }
}

//That method is used to prevent additionnal refresh by not triggering API calls when the agenda type is not the one used
const sortRefreshByCalendarType = () : boolean => {return props.changeAgendaType?.includes(PlanningRefreshSource.WeekModelChange) || props.changeAgendaType?.includes(PlanningRefreshSource.DayModelChange)};

const filterSideDateButtonInput = (planningRefreshSourceModifier : PlanningRefreshSource) : boolean => { 
  return((props.changeDate?.includes(PlanningRefreshSource.RightDateChange + planningRefreshSourceModifier) || props.changeDate?.includes(PlanningRefreshSource.LeftDateChange + planningRefreshSourceModifier)))
};

const filterTodayDateButtonInput = () : boolean => {
  return props.changeDate?.includes(PlanningRefreshSource.TodayDateChange) && (sortRefreshByCalendarType() || props.changeAgendaType?.includes(PlanningRefreshSource.DayModelInit))
}

const refreshEvents = async({ initBusinessHour }: { initBusinessHour: boolean }) => {
  errorMessage.value = undefined;
  if (loading.value) {
    return Promise.resolve();
  }

  loading.value = appUserStore.refreshCalendar;
  calendarOptions.value.events = [];

  if (initBusinessHour) {
    calendarOptions.value.businessHours = [{
      daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
      startTime: '00:00:00',
      endTime: '00:00:00'
    }];
  }
  
  if (!appUserStore.refreshOnlyEvent && !props.isSetting) {
    await getBaseAppointmentDuration();
  }

  if (!appUserStore.refreshOnlyEvent) {
    if (props.getBusinessHours) {
      const result: PlanningByPeriodResponse[] = await getPlanning();

      if (result && result.length > 0) {
        calendarOptions.value.businessHours = result.map((planing: PlanningByPeriodResponse) => toBusinessHour(planing));
      }
    } else if (props.startTime && props.endTime) {
      calendarOptions.value.businessHours = [{
        daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
        startTime: props.startTime,
        endTime: props.endTime
      }];
    }
  }

  calendarOptions.value.events = props.calendarView === CalendarView.DAY && props.doctorId ? 
    await props.getEvents(props.doctorId) : 
    await props.getEvents();
    
  calendar.value?.getApi().updateSize();

  loading.value = false;
  emits('refreshed');
}

const refreshEventsWithAutoScroll = async() => {
  await refreshEvents({ initBusinessHour: false });

  if (props.scroll) {
    calendar.value?.getApi().scrollToTime(props.scroll);
  } else if (!props.changeDate?.includes(PlanningRefreshSource.TodayDateChange) && !props.changeDate?.includes(PlanningRefreshSource.RightDateChange) && !props.changeDate?.includes(PlanningRefreshSource.LeftDateChange)) {
    const now = new Date();
    const nowMinusFifteenMinutes = subMinutes(now, 15);
    
    calendar.value?.getApi().scrollToTime({
      hours: getHours(nowMinusFifteenMinutes) ,
      minutes: getMinutes(nowMinusFifteenMinutes)
    });
  }
}

// Go to given date
const goToDate = (date: Date) => {
  calendar.value?.getApi().gotoDate(date);
   if (props.scroll) {
    calendar.value?.getApi().scrollToTime(props.scroll);
  }
}

const eventResize = (eventResizeDoneArg: EventResizeDoneArg) => {
  emits('updateEvent', {id: eventResizeDoneArg.event.id, start: eventResizeDoneArg.event.start, end: eventResizeDoneArg.event.end, selectedAppointmentType: props.selectedAppointmentType});
}

const eventDrop = (eventDropArg: EventDropArg) => {
  // Drag&Drop in same calendar
  emits('updateEvent', {id: eventDropArg.event.id, start: eventDropArg.event.start, end: eventDropArg.event.end, selectedAppointmentType: props.selectedAppointmentType});
}

const eventReceive = (eventReceiveArg: EventReceiveArg) => {
  // Draf&Drop in another calendar
  emits('updateEvent', {id: eventReceiveArg.event.id, start: eventReceiveArg.event.start, end: eventReceiveArg.event.end, selectedAppointmentType: props.selectedAppointmentType});
}

watch(
  () => props.selectedDate,
  () => {
    goToDate(props.selectedDate);
  }
);

watch(
  () => props.refresh,
  async () => {
    if (props.refresh === true) {
      await refreshEvents({ initBusinessHour: false });
    }
  }
);

watch(
  () => props.getBusinessHours,
  async () => {
    if (!props.getBusinessHours && sortRefreshByCalendarType()) {
      await refreshEventsWithAutoScroll();
    }
  }
);

watch(
  () => appUserStore.refreshCalendar, 
  async () => {
    if (appUserStore.refreshCalendar) {
      await refreshEvents({ initBusinessHour: true });
    }
  }
);

watch(
  () => props.changeAppointmentType,
  async () => {
    if (props.changeAppointmentType && sortRefreshByCalendarType()) {
      await refreshEventsWithAutoScroll();
    }
  }
);

watch(
  () => props.changePatient,
  async () => {
    if (props.changePatient && sortRefreshByCalendarType()) {
      await refreshEventsWithAutoScroll();
    }
  }
);

watch(
  () => props.changeDoctor,
  async () => {
    if (props.changeDoctor && sortRefreshByCalendarType()) {
      await refreshEvents({ initBusinessHour: true });
    }
  }
);

watch(
  () => props.changeDoctorList,
  async () => {
    if (props.changeDoctorList && sortRefreshByCalendarType()) {
      await refreshEventsWithAutoScroll();
    }
  }
);

watch(
  () => props.selectedAppointmentType,
  async () => {
    if (!props.selectedAppointmentType) {
      await refreshEvents({ initBusinessHour: false });
    } else if (props.selectedAppointmentType.appointmentTypeDoctorId && props.doctorId && props.selectedAppointmentType.appointmentTypeDoctorId === props.doctorId) {
      if (!currentAppointmentType.value || (currentAppointmentType.value.id !== props.selectedAppointmentType.id)) {
        currentAppointmentType.value = props.selectedAppointmentType;
        await refreshEvents({ initBusinessHour: false });
      }
    } 
  }
);

watch(
  () => props.doctorId,
  async () => {
    if ((!props.selectedAppointmentType && sortRefreshByCalendarType()) 
        || (props.selectedAppointmentType?.appointmentTypeDoctorId && props.doctorId && props.selectedAppointmentType?.appointmentTypeDoctorId === props.doctorId && sortRefreshByCalendarType())){
          await refreshEventsWithAutoScroll();
    }
  }
);

watch(
  () => currInstituteUuid.value,
  async () => {
    await refreshEvents({ initBusinessHour: false });
  }
);

watch(
  () => props.changeDate,
  async () => {
    if ((filterSideDateButtonInput(PlanningRefreshSource.WeekAgendaTypeChangeModifier) && props.changeAgendaType?.includes(PlanningRefreshSource.WeekModelChange)) 
      || (filterSideDateButtonInput(PlanningRefreshSource.DayAgendaTypeChangeModifier) && (props.changeAgendaType?.includes(PlanningRefreshSource.DayModelChange) || props.changeAgendaType?.includes(PlanningRefreshSource.DayModelInit)))
      || filterTodayDateButtonInput()) {
        await refreshEventsWithAutoScroll();  
      }
    }
);

watch(
  () => props.changeAgendaType,
  async () => {
    if (sortRefreshByCalendarType()) {
      await refreshEventsWithAutoScroll();  
    }
  }
);

watch(
  () => props.displaySettings,
  (newVal) => {
    calendarOptions.value.navLinks = newVal;
  }
);

onMounted(async () => {
    calendar.value.options.height = window.innerHeight - props.calcHeight;
    await refreshEventsWithAutoScroll();
    
    if (props.customRef) {
      emits("exposeApi", calendar.value?.getApi());
    }

    await goToDate(props.selectedDate);
    await refreshEvents({ initBusinessHour: true });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(loadingSpinner, {
            logo: false,
            style: 'w-3'
          }),
          _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('common.loading')), 1)
        ]))
      : _createCommentVNode("", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("span", _hoisted_5, _toDisplayString(errorMessage.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(FullCalendar), {
      ref: _ctx.customRef,
      options: calendarOptions.value
    }, {
      eventContent: _withCtx((arg) => [
        _renderSlot(_ctx.$slots, "eventContent", _normalizeProps(_guardReactiveProps(arg)))
      ]),
      _: 3
    }, 8, ["options"])
  ]))
}
}

})